import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

/**
 * DynamicBackground
 * High-performance, luxury SaaS-level dynamic background (Linear / Stripe aesthetic).
 * Optimized for buttery-smooth 60-120fps rendering:
 * - Zero React re-renders on mousemove (direct rAF DOM style updates)
 * - GPU-composited hardware-accelerated transform layers (will-change: transform)
 * - Pure grid-free porcelain backdrop with cohesive emerald, iris, and champagne auras
 */
export default function DynamicBackground({ children }) {
  const spotlightRef = useRef(null);

  useEffect(() => {
    let frameId;
    const handleMouseMove = (e) => {
      if (frameId) cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        if (spotlightRef.current) {
          const x = ((e.clientX / window.innerWidth) * 100).toFixed(1);
          const y = ((e.clientY / window.innerHeight) * 100).toFixed(1);
          spotlightRef.current.style.background = `radial-gradient(650px circle at ${x}% ${y}%, rgba(11, 93, 75, 0.045), rgba(99, 102, 241, 0.03), transparent 70%)`;
        }
      });
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[#F8FAFB]">
      {/* Self-contained CSS for smooth GPU-accelerated blob morphing */}
      <style>{`
        @keyframes saasMorph1 {
          0%, 100% {
            border-radius: 62% 38% 52% 48% / 45% 58% 42% 55%;
          }
          33% {
            border-radius: 42% 58% 38% 62% / 60% 36% 64% 40%;
          }
          66% {
            border-radius: 54% 46% 65% 35% / 40% 65% 35% 60%;
          }
        }

        @keyframes saasMorph2 {
          0%, 100% {
            border-radius: 48% 52% 40% 60% / 55% 45% 55% 45%;
          }
          33% {
            border-radius: 65% 35% 60% 40% / 38% 62% 38% 62%;
          }
          66% {
            border-radius: 38% 62% 45% 55% / 62% 38% 60% 40%;
          }
        }

        @keyframes saasMorph3 {
          0%, 100% {
            border-radius: 50% 50% 62% 38% / 48% 55% 45% 52%;
          }
          33% {
            border-radius: 58% 42% 40% 60% / 35% 60% 40% 65%;
          }
          66% {
            border-radius: 42% 58% 58% 42% / 60% 40% 60% 40%;
          }
        }

        .saas-blob-1 {
          animation: saasMorph1 24s ease-in-out infinite alternate;
          will-change: transform, border-radius;
          transform: translate3d(0, 0, 0);
        }
        .saas-blob-2 {
          animation: saasMorph2 28s ease-in-out infinite alternate;
          will-change: transform, border-radius;
          transform: translate3d(0, 0, 0);
        }
        .saas-blob-3 {
          animation: saasMorph3 26s ease-in-out infinite alternate;
          will-change: transform, border-radius;
          transform: translate3d(0, 0, 0);
        }
      `}</style>

      {/* 1. Interactive Cursor Spotlight (Direct DOM rAF - zero component re-render) */}
      <div
        ref={spotlightRef}
        className="pointer-events-none fixed inset-0 z-0 transition-opacity duration-500 ease-out"
        style={{
          background: `radial-gradient(650px circle at 50% 50%, rgba(11, 93, 75, 0.045), rgba(99, 102, 241, 0.03), transparent 70%)`,
        }}
      />

      {/* 2. Hardware-Accelerated Ambient Aurora Blobs */}
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" style={{ contain: "strict" }}>
        
        {/* Blob 1: Deep Signature Emerald & Slate Jade (Top Left) */}
        <motion.div
          animate={{
            x: [0, 50, -30, 20, 0],
            y: [0, -40, 30, -20, 0],
            scale: [1, 1.1, 0.95, 1.06, 1],
          }}
          transition={{
            duration: 24,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            willChange: "transform",
            background: "radial-gradient(circle, rgba(11, 93, 75, 0.16) 0%, rgba(15, 118, 110, 0.12) 50%, transparent 75%)",
          }}
          className="saas-blob-1 absolute -left-20 -top-20 h-[620px] w-[620px] blur-[110px]"
        />

        {/* Blob 2: Refined Iris & Royal Slate Indigo (AskPurple AI Glow - Top Right) */}
        <motion.div
          animate={{
            x: [0, -60, 30, -40, 0],
            y: [0, 45, -35, 25, 0],
            scale: [1, 1.12, 0.94, 1.08, 1],
          }}
          transition={{
            duration: 28,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            willChange: "transform",
            background: "radial-gradient(circle, rgba(99, 102, 241, 0.13) 0%, rgba(79, 70, 229, 0.10) 50%, transparent 75%)",
          }}
          className="saas-blob-2 absolute -right-20 top-[6%] h-[580px] w-[580px] blur-[120px]"
        />

        {/* Blob 3: Warm Champagne & Platinum Ochre (Luxury Accent - Mid Flow) */}
        <motion.div
          animate={{
            x: [0, 40, -40, 25, 0],
            y: [0, -50, 35, -25, 0],
            scale: [0.96, 1.1, 0.92, 1.05, 0.96],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            willChange: "transform",
            background: "radial-gradient(circle, rgba(200, 155, 60, 0.11) 0%, rgba(217, 119, 6, 0.08) 50%, transparent 75%)",
          }}
          className="saas-blob-3 absolute left-[32%] top-[38%] h-[560px] w-[560px] blur-[115px]"
        />

        {/* Blob 4: Deep Slate Forest & Aquamarine (Bottom Anchor) */}
        <motion.div
          animate={{
            x: [0, -40, 30, -20, 0],
            y: [0, 35, -30, 20, 0],
            scale: [1, 1.12, 0.96, 1.06, 1],
          }}
          transition={{
            duration: 26,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            willChange: "transform",
            background: "radial-gradient(circle, rgba(6, 78, 59, 0.14) 0%, rgba(13, 148, 136, 0.10) 50%, transparent 75%)",
          }}
          className="saas-blob-1 absolute -right-20 -bottom-20 h-[600px] w-[600px] blur-[120px]"
        />

      </div>

      {/* 3. Foreground Content Container */}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
